import type { RuntimeTileNode } from '@/plugins/tiles/hierarchy/ExplicitHierarchyProvider'
import type { TileCache, TileMemoryBytes } from '@/plugins/tiles/runtime/TileCache'
import type { TileSelectionResult } from '@/plugins/tiles/runtime/TileSelector'

function isDescendant(node: RuntimeTileNode, ancestor: RuntimeTileNode): boolean {
  for (let parent = node.parent; parent; parent = parent.parent)
    if (parent === ancestor) return true
  return false
}

/** 仅保留有界的已就绪粗层；显示切换仍由 selector 在帧边界完成。 */
export class TileBudgetController {
  readonly stops = new Set<string>()
  private readonly _resumeCapacity = new Map<string, number>()
  private readonly _resumeMemory = new Map<string, TileMemoryBytes>()
  private readonly _fallbacks = new Set<string>()

  constructor(private readonly _cache: TileCache) {}

  get fallbackBytes(): number {
    return [...this._fallbacks].reduce((sum, id) => sum + (this._cache.getEntryBytes(id) ?? 0), 0)
  }

  reset(): void {
    for (const id of this._fallbacks) this._cache.unpin(id, 'fallback')
    this._fallbacks.clear()
    this.clearStops()
  }

  clearStops(): void {
    this.stops.clear()
    this._resumeCapacity.clear()
    this._resumeMemory.clear()
  }

  canRestore(capacity: number, memory?: TileMemoryBytes): boolean {
    return [...this._resumeCapacity].some(
      ([id, threshold]) => capacity >= threshold && this.hasMemory(id, memory)
    )
  }

  /** 只报告当前选中的停止项，离屏历史停止不能驱动自动增额。 */
  requiredCapacity(selectedIds: ReadonlySet<string>): number | undefined {
    let required: number | undefined
    for (const [id, bytes] of this._resumeCapacity)
      if (selectedIds.has(id)) required = Math.min(required ?? Infinity, bytes)
    return required
  }

  restore(capacity: number, isReady: (id: string) => boolean, memory?: TileMemoryBytes): void {
    for (const id of this.stops) {
      if (
        !isReady(id) ||
        (capacity >= (this._resumeCapacity.get(id) ?? Infinity) && this.hasMemory(id, memory))
      ) {
        this.stops.delete(id)
        this._resumeCapacity.delete(id)
        this._resumeMemory.delete(id)
      }
    }
  }

  retainFallbacks(
    displayed: readonly RuntimeTileNode[],
    quota: number,
    isReady: (id: string) => boolean
  ): void {
    const ancestors = new Map<string, RuntimeTileNode>()
    for (const displayedNode of displayed) {
      for (let node: RuntimeTileNode | undefined = displayedNode; node; node = node.parent)
        if (node.description.refine === 'REPLACE' && node.children.length && isReady(node.id))
          ancestors.set(node.id, node)
    }
    const next = new Set<string>()
    let bytes = 0
    for (const node of [...ancestors.values()].sort((a, b) => a.depth - b.depth)) {
      const cost = this._cache.getEntryBytes(node.id) ?? 0
      if (bytes + cost > quota) continue
      // 配额内同时保留中间层；只保留最粗祖先会让局部受阻只能退回整个模型。
      next.add(node.id)
      bytes += cost
    }
    for (const id of next) if (!this._fallbacks.has(id)) this._cache.pin(id, 'fallback')
    for (const id of this._fallbacks) if (!next.has(id)) this._cache.unpin(id, 'fallback')
    this._fallbacks.clear()
    for (const id of next) this._fallbacks.add(id)
  }

  relieve(
    selection: TileSelectionResult,
    requiredBytes: number,
    capacity: number,
    getNode: (id: string) => RuntimeTileNode | undefined,
    isReady: (id: string) => boolean,
    memory?: { required: TileMemoryBytes; available: TileMemoryBytes },
    blockedNode?: RuntimeTileNode
  ): boolean {
    const covering = blockedNode
      ? selection.displayed.find((node) => isDescendant(blockedNode, node))
      : undefined
    const candidates = new Map<string, RuntimeTileNode>()
    for (const selected of [...selection.displayed, ...selection.desired]) {
      for (let node = selected.parent; node; node = node.parent)
        if (node.description.refine === 'REPLACE' && isReady(node.id) && !this.stops.has(node.id))
          candidates.set(node.id, node)
    }
    const entries = this._cache.entries()
    const needed = requiredBytes - capacity
    let best:
      | {
          node: RuntimeTileNode
          freed: number
          cpuBytes: number
          gpuBytes: number
          score: number
          local: boolean
        }
      | undefined
    for (const node of candidates.values()) {
      let freed = 0
      let cpuBytes = 0,
        gpuBytes = 0
      for (const entry of entries) {
        const child = getNode(entry.id)
        if (entry.protected && child && isDescendant(child, node)) {
          freed += entry.bytes
          cpuBytes += entry.cpuBytes
          gpuBytes += entry.gpuBytes
        }
      }
      const own = entries.find((entry) => entry.id === node.id)
      if (own && !own.protected) {
        freed -= own.bytes
        cpuBytes -= own.cpuBytes
        gpuBytes -= own.gpuBytes
      }
      const needs = [
        needed,
        ...(memory
          ? [
              memory.required.cpuBytes - memory.available.cpuBytes,
              memory.required.gpuBytes - memory.available.gpuBytes,
            ]
          : []),
      ]
      const gains = [freed, cpuBytes, gpuBytes]
      // 已有覆盖的细化失败只停止该分支，不为它牺牲其它已显示区域。
      // 即使尚无已加载孩子可释放，也可保留当前父层并等待真实容量增长。
      const local =
        !!covering &&
        !!blockedNode &&
        isDescendant(blockedNode, node) &&
        (node === covering || isDescendant(node, covering)) &&
        capacity + freed >= 0 &&
        (!memory ||
          (memory.available.cpuBytes + cpuBytes >= 0 && memory.available.gpuBytes + gpuBytes >= 0))
      const score = needs.reduce(
        (sum, need, index) => sum + (need > 0 ? Math.min(1, Math.max(0, gains[index]) / need) : 0),
        0
      )
      if (score <= 0 && !local) continue
      if (
        !best ||
        (local && !best.local) ||
        (local && best.local && node.depth > best.node.depth) ||
        (!local &&
          !best.local &&
          (score > best.score || (score === best.score && freed < best.freed)))
      )
        best = { node, freed, cpuBytes, gpuBytes, score, local }
    }
    if (!best) return false
    for (const id of this.stops) {
      const node = getNode(id)
      if (node && isDescendant(node, best.node)) {
        this.stops.delete(id)
        this._resumeCapacity.delete(id)
        this._resumeMemory.delete(id)
      }
    }
    this.stops.add(best.node.id)
    // 回粗释放的同一块空间不足以再次细化，避免预算边缘往返加载。
    this._resumeCapacity.set(best.node.id, capacity + best.freed + requiredBytes)
    if (memory)
      this._resumeMemory.set(best.node.id, {
        cpuBytes: Number.isFinite(memory.available.cpuBytes)
          ? memory.available.cpuBytes + best.cpuBytes + memory.required.cpuBytes
          : 0,
        gpuBytes: Number.isFinite(memory.available.gpuBytes)
          ? memory.available.gpuBytes + best.gpuBytes + memory.required.gpuBytes
          : 0,
      })
    return true
  }

  private hasMemory(id: string, available?: TileMemoryBytes): boolean {
    const required = this._resumeMemory.get(id)
    return (
      !required ||
      !available ||
      (available.cpuBytes >= required.cpuBytes && available.gpuBytes >= required.gpuBytes)
    )
  }
}
